// NFC Service for Prepaid Visa Card Payments
// Note: Web NFC API has limited browser support (mainly Chrome on Android)

interface NFCPaymentData {
  cardNumber: string;
  expiryDate: string;
  cvv: string;
  cardholderName: string;
  tokenId: string;
}

interface NFCService {
  isNFCSupported(): boolean;
  isNFCEnabled(): Promise<boolean>;
  writePaymentData(paymentData: NFCPaymentData): Promise<void>;
  readPaymentData(): Promise<NFCPaymentData>;
  requestNFCPermission(): Promise<boolean>;
}

class WebNFCService implements NFCService {
  private nfc: any;

  constructor() {
    // Check if Web NFC API is available
    this.nfc = (window as any).NDEFReader ? new (window as any).NDEFReader() : null;
  }

  isNFCSupported(): boolean {
    return !!(window as any).NDEFReader;
  }

  async isNFCEnabled(): Promise<boolean> {
    if (!this.isNFCSupported()) return false;
    
    try {
      // Try to scan to check if NFC is enabled
      await this.nfc.scan();
      return true;
    } catch (error: any) {
      console.warn('NFC not enabled or available:', error);
      return false;
    }
  }

  async requestNFCPermission(): Promise<boolean> {
    if (!this.isNFCSupported()) return false;

    try {
      // Request NFC permission
      const permission = await navigator.permissions.query({ name: 'nfc' as any });
      return permission.state === 'granted';
    } catch (error) {
      console.warn('NFC permission request failed:', error);
      return false;
    }
  }

  async writePaymentData(paymentData: NFCPaymentData): Promise<void> {
    if (!this.nfc) throw new Error('NFC not supported');

    try {
      // Create NDEF message with payment data
      // Note: This is a simplified example - real implementation would need proper EMV formatting
      const paymentRecord = {
        recordType: "text",
        data: JSON.stringify({
          type: 'vaiu_payment',
          cardNumber: paymentData.cardNumber,
          expiryDate: paymentData.expiryDate,
          cardholderName: paymentData.cardholderName,
          tokenId: paymentData.tokenId,
          timestamp: Date.now()
        })
      };

      await this.nfc.write({
        records: [paymentRecord]
      });

      console.log('✅ Payment data written to NFC');
    } catch (error) {
      console.error('❌ Failed to write NFC payment data:', error);
      throw error;
    }
  }

  async readPaymentData(): Promise<NFCPaymentData> {
    if (!this.nfc) throw new Error('NFC not supported');

    return new Promise((resolve, reject) => {
      this.nfc.addEventListener('reading', ({ message, serialNumber }: any) => {
        try {
          const record = message.records[0];
          const decoder = new TextDecoder();
          const data = JSON.parse(decoder.decode(record.data));
          
          if (data.type === 'vaiu_payment') {
            resolve({
              cardNumber: data.cardNumber,
              expiryDate: data.expiryDate,
              cvv: '', // CVV not stored for security
              cardholderName: data.cardholderName,
              tokenId: data.tokenId
            });
          } else {
            reject(new Error('Invalid payment data'));
          }
        } catch (error) {
          reject(error);
        }
      });

      this.nfc.scan().catch(reject);
    });
  }
}

// Mobile-specific NFC implementation would go here
class MobileNFCService implements NFCService {
  isNFCSupported(): boolean {
    // Would check for Cordova/Capacitor NFC plugins
    return !!(window as any).nfc || !!(window as any).NFC;
  }

  async isNFCEnabled(): Promise<boolean> {
    // Implementation for mobile NFC check
    return false;
  }

  async requestNFCPermission(): Promise<boolean> {
    // Implementation for mobile NFC permissions
    return false;
  }

  async writePaymentData(paymentData: NFCPaymentData): Promise<void> {
    // Implementation for mobile NFC write
    throw new Error('Mobile NFC not implemented');
  }

  async readPaymentData(): Promise<NFCPaymentData> {
    // Implementation for mobile NFC read
    throw new Error('Mobile NFC not implemented');
  }
}

// Factory function to get appropriate NFC service
export const createNFCService = (): NFCService => {
  const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  
  if (isMobile) {
    return new MobileNFCService();
  } else {
    return new WebNFCService();
  }
};

export default createNFCService();
export type { NFCPaymentData, NFCService }; 